import React, { useEffect, useMemo, useState } from 'react';
import { Box } from '@mui/material';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import _ from 'lodash';
import { COLORS } from './tokens';
import useAppInfo from '../../../hooks/appInfo.hook';
import { productsService } from '../../../services/products.service';
import { PLATFORMS } from '../../../utils/constants';
import { createWixFilter, createWixPaging, createWixQuery, createWixSortArray } from '../../../utils/wixTools';
import { categoriesServices } from '../../../services/categories.service';
import { useOnboarding } from '../../../contexts/onboarding.context';
import ecommercePlatforms from '../../../utils/ecommercePlatforms';

const HEADER_HEIGHT = 52;
const TAB_BAR_HEIGHT = 54;

// A store with a long catalogue would fill the carousel with categories nobody
// scrolls to — the first ones are enough, both on the mock and on the theme it saves.
const MAX_CAROUSEL_CATEGORIES = 5;

// The mock is not meant to be scrolled by hand — keep the bars out of the frame.
const hiddenScrollbar = {
    scrollbarWidth: 'none',
    msOverflowStyle: 'none',
    '&::-webkit-scrollbar': { display: 'none' }
};

export function useMockAppPreview() {
    const { data } = useOnboarding();
    const { appInfo } = useAppInfo();

    const isWebview = [PLATFORMS.WEBVIEW, PLATFORMS.LOJAINTEGRADA].includes(appInfo.platformId);
    const isIframeAllowed = _.get(ecommercePlatforms.getById(data.ecommercePlatformId), 'isIframeAllowed', false);
    const storeUrl = data.storeUrl;

    return {
        isHidden: isWebview && (!isIframeAllowed || !storeUrl),
        isWebview,
        storeUrl
    };
}

export default function MockAppHome({ storeName, primaryColor = COLORS.green, homeScreenSections, themeSettings, logoUrl, bannerUrl, setUpdatedCategories }) {
    const { isHidden, isWebview, storeUrl } = useMockAppPreview();

    const findSetting = (key) => (themeSettings || []).find(setting => setting.key === key);

    if (isHidden) {
        return null;
    }

    if (isWebview) {
        const tabBar = findSetting('tabBar');

        return (
            <Box
                sx={{
                    width: '100%',
                    height: '100%',
                    pb: `${TAB_BAR_HEIGHT}px`,
                    backgroundColor: '#fff',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden'
                }}
            >
                {/* The webview theme hides the header, so the site starts at the top */}
                <Box
                    component="iframe"
                    title={`Prévia da ${storeName || 'loja'}`}
                    src={storeUrl}
                    scrolling="no"
                    sx={{
                        flex: 1,
                        width: '100%',
                        border: 0,
                        display: 'block',
                        // Like the rest of the mock, this is a picture of the app,
                        // not the app — the frame must not capture clicks or scroll.
                        pointerEvents: 'none'
                    }}
                />

                <TabBar
                    primaryColor={primaryColor}
                    settings={tabBar}
                />
            </Box>
        );
    }

    return (
        <Box sx={{ width: '100%', height: '100%', backgroundColor: '#fff', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <Header
                logoUrl={logoUrl}
                primaryColor={primaryColor}
                settings={findSetting('header')}
                storeName={storeName}
            />

            <HomeScreen bannerUrl={bannerUrl} homeScreenSections={homeScreenSections} primaryColor={primaryColor} setUpdatedCategories={setUpdatedCategories} />

            <TabBar primaryColor={primaryColor} settings={findSetting('tabBar')} />
        </Box>
    );
}

function Header({ logoUrl, primaryColor, settings, storeName }) {
    const options = _.get(settings, 'options', {});
    const backgroundColor = options.backgroundColor
    const logo = logoUrl || _.get(options, 'logo.url', '');

    return (
        <Box
            sx={{
                flex: 'none',
                height: HEADER_HEIGHT,
                px: '12px',
                backgroundColor,
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                gap: 1
            }}
        >
            <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                <FontAwesomeIcon icon={['fas', 'bars']} style={{ fontSize: 20, color: primaryColor }} />
            </Box>

            <Box sx={{ flex: '0 1 auto', minWidth: 0, display: 'flex', justifyContent: 'center' }}>
                {logo ? (
                    <Box
                        component="img"
                        alt="logo"
                        src={logo}
                        sx={{ height: 30, maxWidth: 130, objectFit: 'contain' }}
                    />
                ) : (
                    <Box
                        component="p"
                        sx={{
                            m: 0,
                            color: primaryColor,
                            fontSize: 15,
                            fontWeight: 700,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                        }}
                    >
                        {storeName}
                    </Box>
                )}
            </Box>

            <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'flex-end', gap: 1 }}>
                <FontAwesomeIcon icon={['fas', 'search']} style={{ fontSize: 20, color: primaryColor }} />
                <FontAwesomeIcon icon={['fas', 'shopping-cart']} style={{ fontSize: 20, color: primaryColor }} />
            </Box>
        </Box>
    );
}

function TabBar({ primaryColor, settings }) {
    const options = _.get(settings, 'options', {});

    const themeTabs = _.get(settings, 'content', []).filter(tab => tab.isVisible !== false);

    const iconColor = options.iconColor || '#939393';

    const activeIconColor = primaryColor || '#000';

    return (
        <Box
            sx={{
                flex: 'none',
                height: TAB_BAR_HEIGHT,
                backgroundColor: options.backgroundColor || '#fff',
                borderTop: `1px solid ${COLORS.divider}`,
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0
            }}
        >
            {themeTabs.map((tab, index) => (
                <Box
                    key={`tbb-${index}`}
                    sx={{
                        flex: 1,
                        minWidth: 0,
                        px: '2px',
                        color: index === 0 ? activeIconColor : iconColor,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}
                >
                    {tab.icon &&
                        <FontAwesomeIcon fixedWidth icon={[tab.icon.style, tab.icon.name]} style={{ fontSize: 18 }} />
                    }

                    <Box
                        component="p"
                        sx={{
                            m: 0,
                            mt: '4px',
                            fontSize: 11,
                            maxWidth: '100%',
                            textAlign: 'center',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                        }}
                    >
                        {tab.title}
                    </Box>
                </Box>
            ))}
        </Box>
    );
}

function HomeScreen({ bannerUrl, homeScreenSections = [], primaryColor, setUpdatedCategories }) {

    const getSectionComponent = (section, index) => {
        switch (section.key) {
            case 'banner':
                return <Banner data={section} key={`psh-${index}`} url={bannerUrl} />;
            case 'categoriesCarousel':
                return <CategoriesCarousel data={section} key={`psh-${index}`} primaryColor={primaryColor} setUpdatedCategories={setUpdatedCategories} />;
            case 'productCarousel':
                return <ProductCarousel data={section} key={`psh-${index}`} primaryColor={primaryColor} />;
            default:
                return null;
        }
    }

    return (
        <Box sx={{ flex: 1 }}>
            {homeScreenSections.map((section, index) => {
                return getSectionComponent(section, index);
            })}
        </Box>
    );
}

function Banner({ data, url }) {
    const options = data.options || {};

    // Same contract as the app: a banner without an image is not rendered.
    const src = url || options.url;

    if (!src) {
        return null;
    }

    const wideRatio = parseInt(options.wideRatio, 10) || 100;
    const width = parseInt(options.width, 10);
    const height = parseInt(options.height, 10);

    // The theme's ratio belongs to the theme's own image — a sent one keeps its own.
    const aspectRatio = !url && width && height ? `${width} / ${height}` : undefined;

    return (
        <Box sx={{ width: `${wideRatio}%` }}>
            <img
                alt="banner"
                src={src}
                style={{
                    display: 'block',
                    width: '100%',
                    aspectRatio
                }}
            />
        </Box>
    );
}

function CategoriesCarousel({ data, primaryColor, setUpdatedCategories }) {
    const fetched = useSectionCategories();

    // Memoized so the effect below only runs when the categories themselves change.
    const categories = useMemo(() => fetched.slice(0, MAX_CAROUSEL_CATEGORIES), [fetched]);

    // The screens that only preview the mock don't pass the setter down.
    useEffect(() => {
        if (setUpdatedCategories) {
            const updatedCategories = categories.map(c => {
                return {
                    title: c.title,
                    categoryId: c.id
                }
            })
            setUpdatedCategories(updatedCategories);
        }
    }, [categories, setUpdatedCategories]);

    const content = _.isEmpty(categories)
        ? (data.content || [])
        : categories;

    return (
        <Box sx={{ my: 1.25 }}>
            <ScrollContainer>
                {content.map((category, index) => (
                    <Box sx={{ position: 'relative', flex: 'none' }} key={`cat-img-${index}`}>
                        <Box
                            sx={{
                                width: 80,
                                height: 80,
                                borderRadius: '50%',
                                mx: '4px',
                                overflow: 'hidden',
                                position: 'relative',
                                backgroundColor: primaryColor,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            <Box
                                component="p"
                                sx={{
                                    color: '#fff',
                                    fontSize: 12,
                                    textAlign: 'center'
                                }}
                            >
                                {category.name}
                            </Box>

                        </Box>
                    </Box>
                ))}
            </ScrollContainer>
        </Box>
    );
}

function ProductCarousel({ data, primaryColor }) {
    const options = data.options || {};
    const products = useSectionProducts(options);

    return (
        <Box>
            <Box
                component="p"
                sx={{ color: primaryColor, fontSize: 16, fontWeight: 700, m: 0, pt: 1.25, px: '5px', textAlign: 'left' }}
            >
                {options.carouselTitle}
            </Box>

            <ScrollContainer>
                {products.map(product => (
                    <Box sx={{ width: '50%', minWidth: '50%', px: '5px' }} key={`phc-${product.id}`}>
                        <ProductCard product={product} primaryColor={primaryColor} />
                    </Box>
                ))}
            </ScrollContainer>
        </Box>
    );
}

function useSectionProducts(options) {
    const { appInfo } = useAppInfo();
    const { accessToken, platformId, storeId } = appInfo;
    const numberOfProducts = parseInt(_.get(options, 'numberOfProducts', 6), 10) || 6;
    const { categoryId, defaultSorting, productsOrigin, term } = options;

    const [products, setProducts] = useState(() => buildPlaceholderProducts(numberOfProducts));

    useEffect(() => {
        if (!platformId || !storeId) {
            return;
        }

        let filters = {};

        switch (platformId) {
            case PLATFORMS.BAGY:
            case PLATFORMS.TRAY:
                filters = {
                    limit: numberOfProducts,
                    sort: 'created_desc'
                };
                break;
            case PLATFORMS.IRROBA:
                filters = {
                    per_page: numberOfProducts,
                    sort_by: 'release-desc'
                };
                break;
            case PLATFORMS.WIX:
                filters = createWixQuery(
                    createWixFilter(categoryId ? { 'collections.id': categoryId } : {}),
                    createWixPaging(1, numberOfProducts),
                    createWixSortArray(['createdDate-desc'])
                );
                break;
            case PLATFORMS.NUVEMSHOP:
                filters = {
                    per_page: numberOfProducts,
                    sort_by: 'created-descending'
                };
                break;
            default:
                filters = {
                    per_page: numberOfProducts,
                    sort_by: _.isEmpty(defaultSorting) ? 'created-descending' : defaultSorting
                };
                break;
        }

        let cancelled = false;

        productsService.getProducts(platformId, storeId, { filters }, accessToken)
            .then(response => {
                if (!cancelled) {
                    const fetched = _.get(response, 'products', []);
                    setProducts(_.isEmpty(fetched)
                        ? buildPlaceholderProducts(numberOfProducts)
                        : fetched.slice(0, numberOfProducts));
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setProducts(buildPlaceholderProducts(numberOfProducts));
                }
            })

        return () => { cancelled = true }
    }, [accessToken, categoryId, defaultSorting, numberOfProducts, platformId, productsOrigin, storeId, term]);

    return products;
}

function useSectionCategories() {
    const { appInfo } = useAppInfo();
    const { accessToken, platformId, storeId } = appInfo;

    const [categories, setCategories] = useState([]);

    useEffect(() => {

        let cancelled = false;

        const filters = [PLATFORMS.TRAY, PLATFORMS.BAGY].includes(platformId) ? { has_product: 1 } : {};

        categoriesServices.getAllCategories(platformId, storeId, accessToken, { filters, preserveOrder: true })
            .then(fetched => {
                if (!cancelled) {
                    setCategories(fetched);
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setCategories([]);
                }
            });

        return () => { cancelled = true }
    }, [accessToken, platformId, storeId]);


    // Memoized so consumers can depend on the identity of this array.
    return useMemo(() => categories.map(categorie => {
        return {
            ...categorie,
            title: categorie.name
        }
    }), [categories]);
}

function buildPlaceholderProducts(numberOfProducts) {
    return Array.from({ length: numberOfProducts }, (item, index) => ({ id: `ph-${index}`, isPlaceholder: true }));
}

function ProductCard({ product, primaryColor }) {
    const imageSize = { width: '100%', aspectRatio: '1 / 1' }
    const imageUrl = _.get(product, 'images[0].imageUrl', '');

    // Catalogues often carry products whose image link is broken — without this the
    // browser would render its own broken-image icon and the alt text inside the mock.
    const [hasImageError, setHasImageError] = useState(false);

    useEffect(() => {
        setHasImageError(false);
    }, [imageUrl]);

    if (product.isPlaceholder) {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', borderWidth: 1, borderColor: COLORS.divider, borderStyle: 'solid', borderRadius: '3px' }}>
                <Box sx={{ ...imageSize, backgroundColor: COLORS.surfaceMuted, borderRadius: '3px' }} />
                <Box sx={{ flex: 1, p: 1 }}>
                    <Box sx={{ height: 10, borderRadius: '3px', backgroundColor: COLORS.surfaceMuted, mb: 0.75 }} />
                    <Box sx={{ height: 10, width: '60%', borderRadius: '3px', backgroundColor: COLORS.surfaceMuted, mb: 1.25 }} />
                    <Box sx={{ height: 12, width: '45%', borderRadius: '3px', backgroundColor: COLORS.surfaceDisabled }} />
                </Box>
            </Box>
        );
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', borderWidth: 1, borderColor: COLORS.divider, borderStyle: 'solid', borderRadius: '3px' }}>
            {(_.isEmpty(imageUrl) || hasImageError) ? (
                <Box
                    sx={{
                        ...imageSize,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: COLORS.surfaceMuted,
                        borderRadius: '3px'
                    }}
                >
                    <ImageOutlinedIcon sx={{ color: COLORS.surfaceDisabled, fontSize: 32 }} />
                </Box>
            ) : (
                <Box
                    component="img"
                    alt={product.name || ''}
                    src={imageUrl}
                    onError={() => setHasImageError(true)}
                    sx={{ ...imageSize, borderRadius: '3px' }}
                />
            )}

            <Box sx={{ flex: 1, minWidth: 0, p: 1, pt: 0 }}>
                <Box
                    component="p"
                    sx={{
                        color: primaryColor,
                        fontSize: 14,
                    }}
                >
                    {_.get(product, 'name', '')}
                </Box>

                {_.get(product, 'hasPromotionalPrice', false) && (
                    <Box
                        component="p"
                        sx={{ m: 0, color: COLORS.textSubtle, fontSize: 11, textDecoration: 'line-through' }}
                    >
                        {product.displayOldPrice}
                    </Box>
                )}

                <Box component="p" sx={{ m: 0, color: COLORS.text, fontSize: 14, fontWeight: 700 }}>
                    {_.get(product, 'displayPrice', '')}
                </Box>
            </Box>
        </Box>
    );
}

function ScrollContainer({ children }) {
    return (
        <Box sx={{ display: 'flex', flexDirection: 'row', width: '100%', overflowX: 'auto', ...hiddenScrollbar }}>
            {children}
        </Box>
    );
}
